import { isSafeRelativeHref } from "./copilotResult";

export const DEMAND_INSIGHTS_KIND = "demand_insights";

const MAX_FEATURES = 5;
const MAX_MATCHES = 3;

const text = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const int = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : null;
};

const normalizeBreakdownRow = (row) => {
  if (!row || typeof row !== "object") return null;
  const label = text(row.label);
  const percent = int(row.percent);
  if (!label || percent === null || percent <= 0) return null;
  return { label, percent: Math.min(percent, 100) };
};

const normalizeBreakdown = (rows) =>
  (Array.isArray(rows) ? rows : []).map(normalizeBreakdownRow).filter(Boolean);

// A match's href is a route gitcha-mcp already built from the same
// `#B<wantAdId>` display id this card also shows, but it arrives over the
// wire like any other server payload - checked against the same
// app-relative-path allowlist normalizeResult's own actions go through,
// rather than trusted just because this trip started server-side.
const normalizeMatch = (match) => {
  if (!match || typeof match !== "object") return null;
  const id = text(match.id);
  if (!id) return null;
  const href = text(match.href);
  return {
    id,
    score: int(match.score),
    href: href && isSafeRelativeHref(href) ? href : null,
    financeType: text(match.financeType),
    timeline: text(match.timeline),
    budget: text(match.budget),
    story: text(match.story),
  };
};

const normalizeMatches = (matches) =>
  (Array.isArray(matches) ? matches : [])
    .slice(0, MAX_MATCHES)
    .map(normalizeMatch)
    .filter(Boolean);

// Same shape check discipline as normalizeResult: the card is only ever
// rendered from a payload that actually has the fields it expects, never
// from "whatever came back" with holes papered over as zeros/blanks.
export const normalizeInsights = (data) => {
  if (!data || data.kind !== DEMAND_INSIGHTS_KIND) return null;

  const stats = data.stats && typeof data.stats === "object" ? data.stats : {};
  const total = int(stats.total);
  if (total === null || total <= 0) return null;

  const budgetSource =
    data.budget && typeof data.budget === "object" ? data.budget : null;
  const budget = budgetSource
    ? {
        askPrice: int(budgetSource.askPrice),
        sweetSpotMin: int(budgetSource.sweetSpotMin),
        sweetSpotMax: int(budgetSource.sweetSpotMax),
        sampleSize: int(budgetSource.sampleSize) || 0,
        isPartialSample: budgetSource.isPartialSample === true,
      }
    : null;

  return {
    kind: DEMAND_INSIGHTS_KIND,
    title: text(data.title) || "This listing",
    askPrice: int(data.askPrice),
    stats: {
      total,
      top: int(stats.top),
      avg: int(stats.avg),
      postedLast30Count: int(stats.postedLast30Count) || 0,
    },
    budget,
    financeMix: normalizeBreakdown(data.financeMix),
    timeline: normalizeBreakdown(data.timeline),
    topFeatures: normalizeBreakdown(data.topFeatures).slice(0, MAX_FEATURES),
    topMatches: normalizeMatches(data.topMatches),
  };
};

export const formatMoney = (value) =>
  typeof value === "number" ? `$${value.toLocaleString()}` : null;

// The report can send a sample too thin to pin down both ends of the sweet
// spot (e.g. one buyer's budget has a max but no min) - render whichever
// side is there instead of "$X to " with nothing after it.
export const formatBudgetRange = (min, max) => {
  const minText = formatMoney(min);
  const maxText = formatMoney(max);
  if (minText && maxText) return `${minText} to ${maxText}`;
  if (maxText) return `Up to ${maxText}`;
  if (minText) return `From ${minText}`;
  return null;
};
